// File: src/components/press/PressPostPreview.js

import React, { useState } from 'react';
import { useProfile } from '../../context/ProfileContext';
import EmbeddedSongLinkCard from './EmbeddedSongLinkCard';

// Helper to safely extract domain names from full URLs
const getDomainName = (url) => {
    try {
        if (!url) return '';
        const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
        return parsed.hostname.replace('www.', '');
    } catch {
        return url;
    }
};

const PressPostPreview = ({ 
    artistName, 
    artistPfp, 
    title, 
    text, 
    mediaFile, 
    mediaUrl, 
    selectedSong, 
    songBoxTitle,
    externalUrl, 
    urlBoxTitle 
}) => {
    const { isDarkMode } = useProfile();
    const [isPlayingPreview, setIsPlayingPreview] = useState(false);

    // Resolve media preview source
    const displayImage = mediaFile ? URL.createObjectURL(mediaFile) : mediaUrl;
    const domain = getDomainName(externalUrl);

    // Dynamic fallback titles
    const defaultArtist = artistName || 'the artist';
    const defaultSongTitle = selectedSong?.title ? `"${selectedSong.title}"` : 'the latest release';
    
    const defaultSongBoxTitle = `Stream ${defaultArtist}'s ${defaultSongTitle} here!`;
    const defaultUrlBoxTitle = `Check out ${defaultArtist}'s link below!`;

    return (
        <div className={`border rounded-3xl p-5 my-4 transition-all duration-300 shadow-md ${
            isDarkMode 
                ? "bg-zinc-950 border-zinc-800 text-white shadow-black/40" 
                : "bg-white border-slate-200 text-slate-900 shadow-slate-100"
        }`}>
            {/* Header Sub-label */}
            <p className={`text-[10px] font-black uppercase tracking-widest mb-4 text-center ${
                isDarkMode ? 'text-zinc-500' : 'text-gray-400'
            }`}>
                Post Preview
            </p>

            {/* 1. MEDIA IMAGE (TOP) */}
            {displayImage && (
                <div className="mb-4 overflow-hidden rounded-2xl border border-zinc-800/20 shadow-sm">
                    <img 
                        src={displayImage} 
                        alt="Post media" 
                        className="w-full h-52 object-cover"
                    />
                </div>
            )}

            {/* 2. TITLE */}
            <h3 className="text-lg font-black tracking-tight mb-2 uppercase italic leading-tight text-slate-900 dark:text-white">
                {title || "[Your Headline Title Here]"}
            </h3>

            {/* 3. BODY TEXT */}
            <p className={`whitespace-pre-wrap text-xs font-semibold leading-relaxed mb-4 ${
                isDarkMode ? 'text-zinc-400' : 'text-gray-600'
            }`}>
                {text || "[Your announcement body will appear here.]"}
            </p>

            {/* 4. EMBEDDED SONG PLAYER NODE */}
            {selectedSong && (
                <EmbeddedSongLinkCard 
                    songId={selectedSong.id} 
                    songBoxTitle={songBoxTitle}
                    songData={selectedSong} // 🌟 Passed directly from builder preview state
                />
            )}

            {/* 5. EXTERNAL URL LINK CARD */}
            {externalUrl && externalUrl.trim() !== '' && (
                <div className="mb-4 space-y-1.5">
                    {/* 🌟 Custom URL Box Title: Bold, normal capitalization, ONLY renders if user types a title */}
                    {urlBoxTitle && urlBoxTitle.trim() !== '' && (
                        <p className="text-xs font-bold text-slate-900 dark:text-white">
                            {urlBoxTitle}
                        </p>
                    )}

                    <a
                        href={externalUrl.startsWith('http') ? externalUrl : `https://${externalUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center justify-between p-3.5 rounded-2xl border transition-all group cursor-pointer ${
                            isDarkMode 
                                ? "bg-zinc-900/60 border-zinc-800 hover:border-[#ff00cc]/50 hover:bg-zinc-900" 
                                : "bg-slate-50 border-slate-200 hover:border-[#ff00cc]/50 hover:bg-slate-100"
                        }`}
                    >
                        <div className="flex items-center space-x-3 overflow-hidden pr-2">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border ${
                                isDarkMode ? "bg-zinc-950 border-zinc-800 text-[#ff00cc]" : "bg-white border-slate-200 text-[#ff00cc]"
                            }`}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.007 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.997 0 0121 12c0 .778-.099 1.533-.284 2.253m-17.432 0A8.959 8.959 0 013 12c0-.778.099-1.533.284-2.253" />
                                </svg>
                            </div>
                            <div className="overflow-hidden">
                                <p className={`text-xs font-bold truncate group-hover:text-[#ff00cc] transition-colors ${
                                    isDarkMode ? "text-white" : "text-slate-900"
                                }`}>
                                    {externalUrl}
                                </p>
                                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate">
                                    {domain || 'External Link'}
                                </p>
                            </div>
                        </div>

                        <div className="flex-shrink-0 text-slate-400 group-hover:text-[#ff00cc] transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                            </svg>
                        </div>
                    </a>
                </div>
            )}

            {/* 6. ARTIST PROFILE PICTURE & NAME ROW */}
            <div className="flex items-center pt-3 border-t border-slate-100 dark:border-zinc-800/80 mb-4">
                <img 
                    src={artistPfp || 'https://via.placeholder.com/40'} 
                    alt={artistName || 'Artist'} 
                    className="w-9 h-9 rounded-full mr-3 object-cover border border-slate-200 dark:border-zinc-800" 
                />
                <div>
                    <p className="font-black text-xs tracking-tight text-slate-900 dark:text-white">
                        {artistName || 'Artist Name'}
                    </p>
                    <p className="text-[9px] font-bold text-slate-400 dark:text-zinc-500">
                        Just now
                    </p>
                </div>
            </div>

            {/* 7. ACTION BUTTON FOOTER */}
            <div className="grid grid-cols-2 gap-2 pt-1">
                <button 
                    type="button"
                    className={`py-2 px-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-sm ${
                        isDarkMode 
                            ? 'bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800' 
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200'
                    }`}
                >
                    Subscribe
                </button>

                <button 
                    type="button"
                    className="py-2 px-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all shadow-md bg-[#ff00cc] text-white hover:brightness-110"
                >
                    Share
                </button>
            </div>
        </div>
    );
};

export default PressPostPreview;